import { useEffect, useState } from "react";

import { meterLevel } from "@/lib/audio/MicProcessor";

const INTERVAL_MS = 50;

const DECAY = 0.7;

export function useMicLevel(
  getTrack: () => MediaStreamTrack | null,
  active: boolean,
): { level: number; listening: boolean } {
  const [level, setLevel] = useState(0);
  const [listening, setListening] = useState(false);

  useEffect(() => {
    if (!active) return undefined;

    const Ctor: typeof AudioContext | undefined =
      globalThis.AudioContext ??
      (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return undefined;

    let context: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let analyser: AnalyserNode | null = null;
    let clone: MediaStreamTrack | null = null;

    let watched: MediaStreamTrack | null = null;
    let samples = new Float32Array(0);

    const detach = () => {
      source?.disconnect();
      analyser?.disconnect();

      clone?.stop();
      source = null;
      analyser = null;
      clone = null;
      watched = null;
    };

    const timer = setInterval(() => {
      const track = getTrack();
      if (track === null || track.readyState !== "live") {
        if (watched !== null) detach();
        setListening(false);
        setLevel(0);
        return;
      }

      if (track !== watched) {
        detach();
        try {
          clone = track.clone();
        } catch {
          return;
        }
        clone.enabled = true;
        context ??= new Ctor();
        void context.resume().catch(() => undefined);
        analyser = context.createAnalyser();
        analyser.fftSize = 1024;
        source = context.createMediaStreamSource(new MediaStream([clone]));
        source.connect(analyser);

        samples = new Float32Array(analyser.fftSize);
        watched = track;
        setListening(true);
      }

      if (analyser === null) return;
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += sample * sample;
      const next = meterLevel(Math.sqrt(sum / samples.length));
      setLevel((previous) => (next > previous ? next : previous * DECAY + next * (1 - DECAY)));
    }, INTERVAL_MS);

    return () => {
      clearInterval(timer);
      detach();
      void context?.close().catch(() => undefined);
    };
  }, [getTrack, active]);

  return { level: active ? level : 0, listening: active && listening };
}
